import { ChangeDetectorRef, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { AuthService } from '../../modules/auth/services/auth.service';
import { UserPermission } from '../../modules/auth/enums/user-permission.enum';
import { firstValueFrom, timer } from 'rxjs';
import { MatExpansionPanel } from '@angular/material/expansion';
import { ConfirmDialogService } from '../../../common-modules/services/confirm-dialog.service';
import { Router } from '@angular/router';
import { FeatureFlagsService } from '../../services/feature-flags.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { OrganizationsService } from '../../modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../modules/mdr-dashboards/services/mdr-dashboards.service';
import { SidenavService } from '../../services/sidenav/sidenav.service';
import { CustomerQuestionnaireService } from '../../modules/customer-questionnaire/services/customer-questionnaire.service';
import { OrganizationData } from '../../modules/organizations/interfaces/OrganizationData';
import { ConfigService } from '../../services/config/config.service';
import _ from 'lodash';
import { AppRoutesEnum } from '../../modules/auth/enums/app-routes.enum';
import { NotificationsService } from '../../modules/notifications/services/notifications.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CompaniesService } from '../../modules/companies/services/companies.service';
import { SecurityaddonsService } from '../../modules/securityaddons/services/securityaddons.service';
import { CompanyDetailsInterface } from '../../modules/companies/interfaces/company-details.interface';
import { IdentityService } from '../../modules/auth/services/identity.service';
import { OauthService } from '../../modules/auth/services/oauth.service';
import { CompanyPortalServices, themeConfig } from '../../modules/auth/config/appConfig';
import { CompanyDataModel } from '../../modules/companies/company-data-model.model';
import { HttpClient } from '@angular/common/http';
import { ToastService } from '../../../common-modules/services/toast.service';

interface ExpansionPanelConfig {
    name: string;
    expanded: boolean;
    children: [];
}

type SidenavButton = {
    show: boolean;
    text: string;
    icon: string;
    id: string;
    link: () => AppRoutesEnum;
    roles: UserPermission[];
    newTab?: boolean;
    isExpandable?: boolean;
    buttons?: SidenavButton[];
    notifications?: string;
};

interface NotificationsInterface {
    name: string;
    value: { count: string; banner: string };
}

@Component({
    selector: 'app-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss'],
    animations: [
        trigger('highlightNotifications', [
            // ...
            state(
                'highlight',
                style({
                    height: '40px',
                    opacity: 1,
                    backgroundColor: '#f2f9fe',
                    color: '#233042',
                    border: '1px solid rgba(2, 126, 230, 0.15)',
                }),
            ),
            state(
                'stop-highlight',
                style({
                    height: '60px',
                    opacity: 1,
                }),
            ),
            transition('highlight => stop-highlight', [animate('1.5s')]),
            transition('stop-highlight => highlight', [animate('1.5s')]),
        ]),
    ],
    standalone: false,
})
export class SidenavComponent implements OnDestroy, OnInit {
    /** get all #panel references from view */
    @ViewChildren('panel') panelList: QueryList<MatExpansionPanel>;

    public isExpanded: boolean | null = null;
    public shouldBeExpanded: boolean;
    public isAnimating: boolean;

    public panelExpandedList: ExpansionPanelConfig[] = [];

    public subscription: Subscription;
    public sidenavCountSubscription: Subscription;

    // we need to declare the enum as property of the component in order to use it inside the template
    public UserPermission = UserPermission;

    public notificationsList: NotificationsInterface[] = [];
    public sumOfAllNotifications: number = 0;
    public allNotifications: any;

    private breachWarrantyButton: SidenavButton = {
        show: this.identityService.isModuleAvailable('breach-warranty'),
        text: $localize`Breach Warranty`,
        icon: '',
        id: 'sidenav-breach-button',
        link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.BREACH_WARRANTY),
        roles: [UserPermission.UsersRead],
    };

    public buttons: SidenavButton[] = [
        {
            show: this.identityService.isModuleAvailable('dashboard'),
            text: $localize`Dashboards`,
            icon: 'ds-icon-dashboard',
            id: 'sidenav-dashboards-button',
            link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.DASHBOARDS),
            roles: [UserPermission.DashboardsRead],
        },
        {
            show:
                this.identityService.isModuleAvailable('alerts-triaged') ||
                this.identityService.isModuleAvailable('investigations') ||
                this.identityService.isModuleAvailable('threat-hunts') ||
                this.identityService.isModuleAvailable('response-actions'),
            text: $localize`Activity`,
            icon: 'ds-icon-activity',
            id: 'sidenav-activity-button',
            link: () => AppRoutesEnum.EMPTY,
            isExpandable: true,
            buttons: [
                {
                    show: this.identityService.isModuleAvailable('alerts-triaged'),
                    text: $localize`Triaged Alerts`,
                    icon: '',
                    id: 'sidenav-triaged-button',
                    link: () => AppRoutesEnum.TRIAGED_ALERTS,
                    roles: [UserPermission.CasesRead],
                },
                {
                    show: this.identityService.isModuleAvailable('investigations'),
                    text: $localize`Investigations`,
                    icon: '',
                    id: 'sidenav-cases-button',
                    link: () => AppRoutesEnum.INVESTIGATIONS,
                    roles: [UserPermission.CasesRead],
                },
                {
                    show: this.identityService.isModuleAvailable('threat-hunts'),
                    text: $localize`Threat Hunts`,
                    icon: '',
                    id: 'sidenav-hunts-button',
                    link: () => AppRoutesEnum.THREAT_HUNTS,
                    roles: [UserPermission.CasesRead],
                },
                {
                    show: this.identityService.isModuleAvailable('response-actions'),
                    text: $localize`Response Actions`,
                    icon: '',
                    id: 'sidenav-response-actions-button',
                    link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.RESPONSE_ACTIONS),
                    roles: [UserPermission.CasesRead],
                },
            ],
            roles: [UserPermission.CasesRead],
        },
        {
            show:
                this.identityService.isModuleAvailable('customer-verification-requests') ||
                this.identityService.isModuleAvailable('recommendations'),
            link: () => AppRoutesEnum.EMPTY,
            text: $localize`Customer Tasks`,
            icon: 'ds-icon-recommendations',
            id: 'sidenav-customer-tasks-button',
            isExpandable: true,
            notifications: 'customer-tasks',
            buttons: [
                {
                    show:
                        this.identityService.isModuleAvailable('customer-verification-requests') &&
                        this.configService.getFeaturesConfig().enable_cvr === 'true',
                    text: $localize`Verification requests`,
                    icon: '',
                    id: 'sidenav-customer-verifications-requests-button',
                    link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.CUSTOMER_VERIFICATION_REQUESTS),
                    roles: [UserPermission.CasesRead],
                    notifications: 'customer-verification-requests',
                },
                {
                    show: this.identityService.isModuleAvailable('recommendations'),
                    text: $localize`Recommendations`,
                    icon: '',
                    id: 'sidenav-recommendations-button',
                    link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.RECOMMENDATIONS),
                    roles: [UserPermission.CasesRead],
                    notifications: 'recommendations',
                },
            ],
            roles: [UserPermission.CasesRead],
        },
        {
            show: this.identityService.isModuleAvailable('support-tickets'),
            text: $localize`Tickets`,
            icon: 'ds-icon-tickets',
            id: 'sidenav-support-tickets-button',
            link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.TICKETS),
            roles: [UserPermission.TicketsRead],
        },
        {
            show: this.identityService.isModuleAvailable('reports'),
            text: $localize`Reports`,
            icon: 'ds-icon-reports',
            id: 'sidenav-reports-button',
            link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.REPORTS),
            roles: [UserPermission.ReportsRead],
        },
        {
            show:
                this.identityService.isModuleAvailable('emergency-contacts') ||
                this.identityService.isModuleAvailable('pre-approved-actions'),
            text: $localize`Service Management`,
            icon: 'ds-icon-service-management',
            id: 'sidenav-service-management-button',
            link: () => AppRoutesEnum.EMPTY,
            isExpandable: true,
            buttons: [
                {
                    show: this.identityService.isModuleAvailable('emergency-contacts'),
                    text: $localize`Emergency Contacts`,
                    icon: '',
                    id: 'sidenav-cases-button',
                    link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.EMERGENCY_CONTACT),
                    roles: [UserPermission.ContactsRead],
                },
                {
                    show: this.identityService.isModuleAvailable('pre-approved-actions'),
                    text: $localize`Pre-Approved Actions`,
                    icon: '',
                    id: 'sidenav-paas-button',
                    link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.PRE_APPROVED_ACTIONS),
                    roles: [UserPermission.PaaRead],
                },
            ],
            roles: [UserPermission.ContactsRead, UserPermission.PaaRead],
        },
        {
            show: this.identityService.isModuleAvailable('users'),
            text: $localize`Users`,
            icon: 'ds-icon-users',
            id: 'sidenav-users-button',
            link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.USERS),
            roles: [UserPermission.UsersRead, UserPermission.ContactsRead],
        },
        {
            show: this.identityService.isModuleAvailable('companies'),
            text: $localize`Companies`,
            icon: 'ds-icon-companies',
            id: 'sidenav-companies-button',
            link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.COMPANIES),
            roles: [UserPermission.CompaniesRead],
        },
        {
            show: this.identityService.isModuleAvailable('terms-and-conditions'),
            text: $localize`Terms and Conditions`,
            icon: 'ds-icon-terms-and-conditions',
            id: 'sidenav-tac-button',
            newTab: false,
            link: () => AppRoutesEnum.TERMS_AND_CONDITIONS,
            roles: [UserPermission.CompaniesRead],
        },
        {
            show:
                this.identityService.isModuleAvailable('companies-onboarding-issues') ||
                this.identityService.isModuleAvailable('companies-offboarding-list'),
            text: $localize`Admin`,
            icon: 'ds-icon-service-management',
            id: 'sidenav-admin-button',
            link: () => AppRoutesEnum.EMPTY,
            isExpandable: true,
            buttons: [
                {
                    show: this.identityService.isModuleAvailable('companies-onboarding-issues'),
                    text: $localize`Onboarding Issues`,
                    icon: '',
                    id: 'sidenav-onboarding-issues-button',
                    link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.ONBORADING_ISSUES),
                    roles: [UserPermission.CompaniesCreate],
                },
                {
                    text: $localize`Monitoring Issues`,
                    icon: '',
                    id: 'sidenav-monitoring-issues-button',
                    link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.MONITORING_ISSUES),
                    roles: [UserPermission.CompaniesCreate],
                    show: this.identityService.isModuleAvailable('companies-monitoring-issues'),
                },
                {
                    show: this.identityService.isModuleAvailable('companies-offboarding-list'),
                    text: $localize`Offboarding List`,
                    icon: '',
                    id: 'sidenav-onboarding-issues-button',
                    link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.OFFBOARDING_LIST),
                    roles: [UserPermission.CompaniesCreate],
                },
                {
                    show: this.identityService.isModuleAvailable('companies-onboarding-history'),
                    text: $localize`Onboarding History`,
                    icon: '',
                    id: 'sidenav-onboarding-history-button',
                    link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.ONBOARDING_HISTORY),
                    roles: [UserPermission.CompaniesCreate],
                },
            ],
            roles: [UserPermission.CompaniesCreate],
        },
    ];
    public backupButtons: SidenavButton[] = [];
    public dynamicButtons = [
        {
            show: this.identityService.isModuleAvailable('documents'),
            text: $localize`Documents`,
            icon: 'ds-icon-documents',
            id: 'sidenav-documents-button',
            link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : AppRoutesEnum.DOCUMENTS),
            roles: [UserPermission.DocumentsRead],
        },
        {
            show: this.identityService.isModuleAvailable('cq'),
            text: $localize`Customer Questionnaire`,
            icon: '',
            id: 'sidenav-cq-button',
            link: () => (!this.authService.isLogged() ? AppRoutesEnum.LOGIN : this.cqRouteBuilder() || AppRoutesEnum.EMPTY),
            roles: [UserPermission.QuestionnaireQuestionsRead, UserPermission.QuestionnaireQuestionWrite],
        },
        this.breachWarrantyButton,
    ];
    public authSubscription: Subscription;
    public orgSubscription: Subscription;
    public companyLoadSubscription: Subscription;
    public highlightSubscription: Subscription;

    public isHighlighted: boolean;
    public isUsernameTooltipDisabled: boolean;
    public showBreachWarranty: boolean;

    constructor(
        public authService: AuthService,
        public companiesService: CompaniesService,
        public identityService: IdentityService,
        public router: Router,
        public featureFlagService: FeatureFlagsService,
        protected confirmDialogService: ConfirmDialogService,
        private organizationService: OrganizationsService,
        private mdrService: MdrDashboardsService,
        public sidenavService: SidenavService,
        private customerQuestionnaireService: CustomerQuestionnaireService,
        private configService: ConfigService,
        private cdr: ChangeDetectorRef,
        private securityAddonsService: SecurityaddonsService,
        public notificationsService: NotificationsService,
        public oauthService: OauthService,
        public http: HttpClient,
        public toastService: ToastService,
    ) {
        this.readIsSidenavExpanded();
        this.readIsExpansionPanelExpanded();
        this.getCompanyLogo();
    }

    private static getPanelObject(): ExpansionPanelConfig[] {
        return JSON.parse(<string>localStorage.getItem('isPanelExpanded'));
    }

    ngOnInit(): void {
        this.highlightSubscription = this.notificationsService.highlightSubject.asObservable().subscribe((value) => {
            this.isHighlighted = true;
            const notifButton = document.getElementById('notifications_button');
            if (notifButton) {
                notifButton.scrollIntoView();
            }
            setTimeout(() => {
                this.isHighlighted = false;
            }, 3500);
        });

        this.backupButtons = _.cloneDeep(this.buttons);

        if (this.sidenavService.sidenavButtons.length > 0) {
            this.buttons = this.sidenavService.sidenavButtons;
        }

        this.getNotifications();

        this.orgSubscription = this.organizationService.selectedOrganization$().subscribe(this.updateSidenavButtonsForOrg.bind(this));
        this.securityAddonsService.breachWarrantySubject.subscribe((showBreachWarranty) => {
            // Force update at least once for when no organization is selected
            if (showBreachWarranty == true && this.showBreachWarranty == false) {
                this.showBreachWarranty = showBreachWarranty;
                this.setupBreachWarrantyButton();
            }
            this.showBreachWarranty = showBreachWarranty;
        });
    }

    ngOnDestroy(): void {
        this.authSubscription?.unsubscribe();
        this.orgSubscription?.unsubscribe();
        this.companyLoadSubscription?.unsubscribe();
    }

    public getNotificationsValueUI(name: string): string {
        const notificationCount = parseInt(this.getNotificationsValue(name), 10);

        return notificationCount > 99 ? '99+' : notificationCount.toString();
    }

    public getNotificationsValue(name: string): string {
        const notificationData = this.notificationsList.find((x) => x.name === name);

        if (notificationData === undefined || notificationData === null) {
            return '0';
        }

        return notificationData.value.count;
    }

    public getNotifications() {
        this.sidenavCountSubscription = this.sidenavService
            .getAllNotifications(this.organizationService.currentlySelectedOrganization()?.id || null)
            .subscribe((notificationObj) => {
                this.sumOfAllNotifications = 0;
                this.allNotifications = notificationObj;
                for (const [key, value] of Object.entries(notificationObj)) {
                    if (this.buttons.find((x) => x.notifications === key)) {
                        // @ts-ignore
                        for (const [notifName, valueOfNotification] of Object.entries(notificationObj[key])) {
                            if (!this.notificationsList.find((notif) => notif.name === notifName)) {
                                this.notificationsList.push({
                                    name: notifName,
                                    value: (valueOfNotification as any).count,
                                });
                                this.sumOfAllNotifications += (valueOfNotification as any).count;
                            } else {
                                // @ts-ignore
                                this.notificationsList[
                                    this.notificationsList.indexOf(
                                        <NotificationsInterface>this.notificationsList.find((x) => x.name === notifName),
                                    )
                                ].value = valueOfNotification as number;
                                this.sumOfAllNotifications += (valueOfNotification as any).count;
                            }
                        }
                    }
                }

                this.sidenavCountSubscription.unsubscribe();
                this.cdr.detectChanges();
            });
    }

    public async publishDynamicButtons(organization: OrganizationData | null) {
        this.buttons = _.cloneDeep(this.backupButtons);
        await this.loadSidenavButtonBasedOnCompanyType(
            'sidenav-documents-button',
            false,
            true,
            5,
            -1,
            organization || this.identityService.company,
        );

        if (
            this.authService.hasAtLeastOneRole([
                UserPermission.QuestionnaireQuestionWrite,
                UserPermission.QuestionnaireAnswerReview,
                UserPermission.QuestionnaireQuestionsRead,
            ])
        ) {
            await this.loadSidenavButtonBasedOnCompanyType(
                'sidenav-cq-button',
                false,
                true,
                2,
                this.buttons.findIndex((x) => x.id === 'sidenav-service-management-button'),
                organization,
            );
        }
    }

    public cqRouteBuilder(): AppRoutesEnum {
        if (!this.authService.hasAtLeastOneRole([UserPermission.QuestionnaireQuestionWrite])) {
            return AppRoutesEnum.CQ_ADMIN;
        }

        if (this.authService.hasAtLeastOneRole([UserPermission.QuestionnaireQuestionWrite, UserPermission.QuestionnaireAnswerReview])) {
            return AppRoutesEnum.CQ_CST;
        }

        return AppRoutesEnum.EMPTY;
    }

    public onLogoutClicked() {
        this.customerQuestionnaireService.getQuestionnaireSubscription?.unsubscribe();

        if (this.identityService.getPortalType() === CompanyPortalServices.Customerzone) {
            console.log('Customerzone logout');
            this.oauthService.logoutUser();
            return;
        }

        this.authService.logout();
    }

    public panelIsClosing(id: string): void {
        let obj = SidenavComponent.getPanelObject();
        const index = this.getPanelIndex(id);

        if (index === null) {
            return;
        }

        obj[index].expanded = false;
        localStorage.setItem('isPanelExpanded', JSON.stringify(obj));
    }

    public panelIsOpening(id: string): void {
        let obj = SidenavComponent.getPanelObject();
        const index = this.getPanelIndex(id);

        if (index === null) {
            return;
        }

        obj[index].expanded = true;
        localStorage.setItem('isPanelExpanded', JSON.stringify(obj));
    }

    public checkPanelExpanded(id: string): boolean {
        let obj = SidenavComponent.getPanelObject();
        const index = this.getPanelIndex(id);

        if (index === null) {
            return false;
        }

        return obj[index].expanded;
    }

    public calculateSubmenuPosition(id: string, floatingMenuId: string): void {
        const target = document.getElementById(id);
        const offsetTop = target ? target.offsetTop : 0;

        const menu = document.getElementById(floatingMenuId);
        if (menu) {
            menu.style.top = String(offsetTop + 'px');
        }
    }

    public expandCollapseSidenav(): void {
        this.shouldBeExpanded = !this.shouldBeExpanded;
        localStorage.setItem('isSidenavExpanded', this.shouldBeExpanded.toString());
        this.isAnimating = true;

        if (this.shouldBeExpanded) {
            this.isExpanded = this.shouldBeExpanded;
            timer(250).subscribe({
                next: () => {
                    this.isAnimating = false;
                },
            });
        } else {
            timer(250).subscribe({
                next: () => {
                    this.isExpanded = this.shouldBeExpanded;
                    this.isAnimating = false;
                },
            });
        }
        if (!this.shouldBeExpanded && this.panelList) {
            this.panelList.forEach((element: MatExpansionPanel) => {
                element.close();
            });
        }
    }

    public checkIfRouteActive(id: string) {
        return document.getElementById(id)?.classList.contains('sidenav-button-active');
    }

    public checkIfUsernameTooltipIsNecessary(event: MouseEvent): void {
        let target: Element | null = event.target as Element;

        if (!target) {
            return;
        }

        this.isUsernameTooltipDisabled = !(target.scrollWidth > target.clientWidth);
    }

    private async updateSidenavButtonsForOrg(organization: OrganizationData | null): Promise<void> {
        await this.publishDynamicButtons(organization ?? this.identityService.company);
        this.setupBreachWarrantyButton();
        this.getNotifications();
        this.cdr.detectChanges();
    }

    private findButtonById(id: string): SidenavButton | undefined {
        const button = this.buttons.find((x) => x.id === id);
        return button;
    }

    private setupBreachWarrantyButton() {
        if (!this.showBreachWarranty) {
            return;
        }

        const servicesButton = this.findButtonById('sidenav-service-management-button');
        if (servicesButton == undefined) {
            return;
        }

        const breachButton = servicesButton.buttons?.find((k) => k.id == this.breachWarrantyButton.id);
        if (!breachButton) {
            servicesButton.buttons?.push(this.breachWarrantyButton);
        }
    }

    private async loadSidenavButtonBasedOnCompanyType(
        buttonId: string,
        keepForLco: boolean,
        keepForAdvanced: boolean,
        index: number,
        parentIndex: number,
        organization: OrganizationData | null,
    ) {
        let organizationData = organization;

        if (organization && !organization?.details && organization?.id) {
            organizationData = await firstValueFrom(this.companiesService.getCompanyDetails(organization.id));
        }

        const org = organizationData || this.identityService.company;
        if (this.mdrService.isLcoCompany(org) && this.mdrService.isPremiumEnterprise(org)) {
            this.takeActionForButton(buttonId, 'add', index, parentIndex);
            return;
        }
        /* if company is NOT lco
         * -> and we need to "see" the button only if selected company is lco then we remove it;
         * - > if we need to "see" the button only if the company is not lco then we add it
         * if company is LCO
         * -> and we need to "see" a button only if the company is lco then we add it
         * -> if we need to "see" a button only if the company is NOT lco then we remove it
         * */
        if (this.mdrService.isPremiumEnterprise(org)) {
            if (keepForLco) {
                this.takeActionForButton(buttonId, 'remove', index, parentIndex);
            } else {
                this.takeActionForButton(buttonId, 'add', index, parentIndex);
            }

            if (keepForAdvanced) {
                this.takeActionForButton(buttonId, 'add', index, parentIndex);
            } else {
                this.takeActionForButton(buttonId, 'remove', index, parentIndex);
            }
        } else {
            if (keepForLco) {
                this.takeActionForButton(buttonId, 'add', index, parentIndex);
            } else {
                this.takeActionForButton(buttonId, 'remove', index, parentIndex);
            }

            if (keepForAdvanced) {
                this.takeActionForButton(buttonId, 'remove', index, parentIndex);
            } else {
                this.takeActionForButton(buttonId, 'add', index, parentIndex);
            }
        }
    }

    private takeActionForButton(buttonId: string, action: 'remove' | 'add', index: number, parentIndex: number) {
        let searchedButton =
            parentIndex > 0
                ? this.buttons[parentIndex]?.buttons?.find((btn) => btn.id === buttonId)
                : this.buttons.find((btn) => btn.id === buttonId);

        const buttonNode = this.dynamicButtons.filter((btn) => btn.id === buttonId)[0];

        if (searchedButton && action === 'remove') {
            if (parentIndex > 0) {
                this.buttons[parentIndex]?.buttons?.splice(this.buttons.indexOf(searchedButton), 1);
            } else {
                this.buttons.splice(this.buttons.indexOf(searchedButton), 1);
            }
        }
        if (!searchedButton && action === 'add') {
            if (parentIndex > 0) {
                this.buttons[parentIndex]?.buttons?.splice(index, 0, buttonNode);
            } else {
                this.buttons.splice(index, 0, buttonNode);
            }
        }

        this.sidenavService.sidenavButtons = this.buttons;
    }

    private readIsSidenavExpanded(): void {
        const isSidenavExpanded = localStorage.getItem('isSidenavExpanded');

        if (isSidenavExpanded === null) {
            localStorage.setItem('isSidenavExpanded', 'true');
            this.shouldBeExpanded = this.isExpanded = true;
            return;
        }

        this.shouldBeExpanded = this.isExpanded = isSidenavExpanded === 'true';
    }

    private readIsExpansionPanelExpanded(): void {
        let isPanelExpanded: ExpansionPanelConfig[] | undefined;
        const storageValue = localStorage.getItem('isPanelExpanded');

        if (typeof storageValue !== 'undefined' && storageValue !== null) {
            isPanelExpanded = storageValue === 'true' || storageValue === 'false' ? undefined : JSON.parse(<string>storageValue);
        }

        /**
         * Initialize the panel list object on login
         * By default we'll set the panels collapsed
         */
        if (isPanelExpanded === undefined) {
            this.buttons.forEach((button: any) => {
                if (button.isExpandable) {
                    let expanded = false;
                    for (let i = 0; i < button.buttons.length; i++) {
                        if (this.router?.url && this.router?.url.includes(button.buttons[i].link)) {
                            expanded = true;
                            break;
                        }
                    }
                    this.panelExpandedList.push({
                        name: button.text,
                        expanded: expanded,
                        children: button.buttons,
                    });
                }
            });
            localStorage.setItem('isPanelExpanded', JSON.stringify(this.panelExpandedList));
            return;
        }

        /**
         * If the user is already logged in, we retrieve the state of the panels
         */
        this.panelExpandedList?.forEach((item: ExpansionPanelConfig, index: number) => {
            if (isPanelExpanded) {
                item.expanded = isPanelExpanded[index].expanded;
            }
        });
    }

    private getPanelIndex(id: string): number | null {
        let index = -1;
        let obj = SidenavComponent.getPanelObject();

        let itemFound = obj?.some((item: ExpansionPanelConfig, i: number) => {
            index = i;
            return item.name === id;
        });

        if (!itemFound) {
            return null;
        }

        return index;
    }

    private getCompanyLogo() {
        const config = themeConfig.find((item) => item.service === this.identityService.getPortalType());
        this.sidenavService.logo = config?.logo || 'assets/images/logo/logo-light.svg';
    }
}
